import React, {
  CSSProperties,
  forwardRef,
  memo,
  useCallback,
  useState,
} from "react";
import { DefaultInputComponentProps } from "react-phone-number-input";
import { useTheme } from "../../hooks/useTheme";
import { addAlpha, Colors } from "../../utils/colors";
import { PhoneErrorType } from "@markit/common.utils";
import { ThemeType } from "@markit/common.types";

interface CustomInputProps extends DefaultInputComponentProps {
  error: PhoneErrorType;
  disabled?: boolean;
  inputStyles?: CSSProperties;
  startAdornment?: JSX.Element;
  endAdornment?: JSX.Element;
  autoCompleteOn?: boolean;
  themeType?: ThemeType;
}

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      value,
      onChange,
      autoCompleteOn,
      error,
      disabled,
      inputStyles,
      startAdornment,
      endAdornment,
      themeType,
      ...rest
    },
    ref
  ) => {
    const { theme } = useTheme(themeType);
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = () => {
      setIsFocused(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    const validatePhoneNumber = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const phoneNumber = e.target.value;
        onChange(phoneNumber);
      },
      [onChange]
    );

    return (
      <div className="ColumnNormal" style={{ flex: 1, gap: 4 }}>
        <div className="AlignedRow" style={{ position: "relative" }}>
          <style>
            {`input:-webkit-autofill,
              input:-webkit-autofill:hover, 
              input:-webkit-autofill:focus {
              -webkit-text-fill-color: ${
                disabled
                  ? addAlpha(theme.PrimaryText.color, 0.7)
                  : theme.PrimaryText.color
              };
              -webkit-box-shadow: 0 0 0px 1000px ${
                theme.SecondaryBG.backgroundColor
              } inset;
            }`}
            {`input::placeholder {
              color: ${Colors.GRAY1};
            }`}
          </style>
          <input
            {...rest}
            ref={ref}
            value={value}
            onChange={validatePhoneNumber}
            onFocus={handleFocus}
            onBlur={handleBlur}
            autoComplete={autoCompleteOn ? "tel" : "off"}
            disabled={disabled}
            style={{
              backgroundColor: theme.SecondaryBG.backgroundColor,
              padding: "10px 14px",
              borderRadius: 12,
              border: "1px solid",
              borderColor: error.isError
                ? theme.ErrorBorderColor.borderColor
                : isFocused
                ? theme.FocusedTextFieldBorder.borderColor
                : disabled
                ? addAlpha(theme.EditableTextFieldBorder.borderColor, 0.7)
                : theme.EditableTextFieldBorder.borderColor,
              caretColor: theme.PrimaryText.color,
              color: disabled
                ? addAlpha(theme.PrimaryText.color, 0.7)
                : theme.PrimaryText.color, // Affects opacity for mobile production
              opacity: disabled ? 0.4 : 1, // Affects opacity only for web mobile browser development
              fontSize: 14,
              flex: 1,
              paddingLeft: startAdornment ? 52 : 14,
              ...inputStyles,
            }}
          />
          {startAdornment ? (
            <div style={{ position: "absolute", left: 14 }}>
              {startAdornment}
            </div>
          ) : null}
          {endAdornment ? (
            <div style={{ position: "absolute", right: 14 }}>
              {endAdornment}
            </div>
          ) : null}
        </div>
        {error.isError ? (
          <span style={{ color: Colors.RED3, fontSize: 12 }}>
            {error.message}
          </span>
        ) : null}
      </div>
    );
  }
);

// Add a display name for the component
CustomInput.displayName = "CustomInput";

export default memo(CustomInput);
